import {
  Badge,
  Button,
  Card,
  Col,
  Descriptions,
  Drawer,
  Modal,
  Form,
  Input,
  List,
  Row,
  Select,
  Space,
  Spin,
  Tooltip,
  Tag,
  Typography,
  message,
} from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { BASE_API_URL } from "../../utils/consts";
import { useAuth } from "../../utils/useAuth";
import { isSuperAdmin } from "../../utils/permissions";
import "./Support.scss";

const { Text, Title, Paragraph } = Typography;
const { TextArea } = Input;

type SupportTicket = {
  _id: string;
  clientId?: { _id: string; name: string };
  userId?: {
    _id: string;
    email: string;
    firstName?: string;
    lastName?: string;
  };
  email?: string;
  subject: string;
  description: string;
  status: "open" | "in_progress" | "closed";
  priority: "low" | "normal" | "high";
  messages: { author: string; message: string; createdAt: string }[];
  createdAt: string;
  updatedAt: string;
};

const statusColors: Record<SupportTicket["status"], string> = {
  open: "blue",
  in_progress: "orange",
  closed: "green",
};

const priorityColors: Record<SupportTicket["priority"], string> = {
  low: "default",
  normal: "cyan",
  high: "red",
};

const Support = () => {
  const { t } = useTranslation();
  const userData = useSelector((state: any) => state.userData.user);
  const authUser: any = useAuth();
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<{ status?: string }>({});
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(
    null
  );
  const [replyLoading, setReplyLoading] = useState(false);
  const [createForm] = Form.useForm();
  const [replyForm] = Form.useForm();
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const parsedAuth = useMemo(() => {
    if (!authUser) return undefined;
    if (typeof authUser === "string") {
      try {
        return JSON.parse(authUser);
      } catch {
        return undefined;
      }
    }
    return authUser;
  }, [authUser]);

  const clientId = userData?.clientId || parsedAuth?.clientId;
  const userId = userData?.id || parsedAuth?._id || parsedAuth?.id;
  const userEmail = userData?.email || parsedAuth?.email;
  const isSuper = isSuperAdmin(userData?.roles);
  const [clientFilter, setClientFilter] = useState<string>("");

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (isSuper) {
        if (clientFilter) params.clientId = clientFilter;
      } else if (clientId) {
        params.clientId = clientId;
      }
      if (filters.status) params.status = filters.status;

      const { data } = await axios.get(`${BASE_API_URL}/support/tickets`, {
        params,
      });
      // Već filtrirano po clientId (za nesuper korisnike), pa prikaži sve za tog klijenta.
      setTickets(data || []);
    } catch (error) {
      console.error(error);
      message.error(t("Failed to load tickets"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, filters.status, userData?.roles]);

  const createTicket = async (values: any) => {
    if (!clientId) {
      message.error(t("Missing client ID"));
      return;
    }

    try {
      setLoading(true);
      await axios.post(`${BASE_API_URL}/support/tickets`, {
        clientId,
        userId,
        email: values.email || userEmail,
        subject: values.subject,
        description: values.description,
        priority: values.priority || "normal",
      });
      message.success(t("Ticket created"));
      createForm.resetFields();
      fetchTickets();
    } catch (error) {
      console.error(error);
      message.error(t("Failed to create ticket"));
    } finally {
      setLoading(false);
    }
  };

  const openDetails = (ticket: SupportTicket) => {
    setSelectedTicket(ticket);
    replyForm.resetFields();
    setDrawerOpen(true);
  };

  const addReply = async (values: any) => {
    if (!selectedTicket) return;
    try {
      setReplyLoading(true);
      await axios.put(
        `${BASE_API_URL}/support/tickets/${selectedTicket._id}/reply`,
        {
          author: userEmail || "user",
          message: values.message,
          status: values.status || selectedTicket.status,
        }
      );
      message.success(t("Reply sent"));
      setDrawerOpen(false);
      fetchTickets();
    } catch (error) {
      console.error(error);
      message.error(t("Failed to send reply"));
    } finally {
      setReplyLoading(false);
    }
  };

  const updateStatusQuick = async (ticket: SupportTicket, status: string) => {
    try {
      await axios.put(`${BASE_API_URL}/support/tickets/${ticket._id}/status`, {
        status,
      });
      message.success(t("Status updated"));
      fetchTickets();
    } catch (error) {
      console.error(error);
      message.error(t("Failed to update status"));
    }
  };

  const confirmDelete = (ticket: SupportTicket) => {
    if (!isSuper) return;
    const doDelete = async () => {
      try {
        setDeletingId(ticket._id);
        await axios.delete(`${BASE_API_URL}/support/tickets/${ticket._id}`);
        message.success(t("Ticket deleted"));
        setDrawerOpen(false);
        fetchTickets();
      } catch (error) {
        console.error(error);
        message.error(t("Failed to delete ticket"));
      } finally {
        setDeletingId(null);
      }
    };

    Modal.confirm({
      title: t("Delete ticket?"),
      icon: <ExclamationCircleOutlined />,
      content: t("This action cannot be undone."),
      okText: t("Delete"),
      okType: "danger",
      cancelText: t("Cancel"),
      onOk: doDelete,
    });
  };

  const statusFilterOptions = [
    { value: "", label: t("All") },
    { value: "open", label: t("Open") },
    { value: "in_progress", label: t("In Progress") },
    { value: "closed", label: t("Closed") },
  ];

  return (
    <div className="settingsTab">
      <Row gutter={[16, 16]}>
        <Col xs={24} md={10} lg={8}>
          <Card title={t("Create Support Ticket")} bordered={false}>
            <Form
              layout="vertical"
              form={createForm}
              initialValues={{
                email: userEmail,
                priority: "normal",
              }}
              onFinish={createTicket}
            >
              <Form.Item
                label={t("Email")}
                name="email"
                rules={[{ type: "email", message: t("Invalid email") }]}
              >
                <Input placeholder={t("Your contact email")} />
              </Form.Item>
              <Form.Item
                label={t("Subject")}
                name="subject"
                rules={[{ required: true, message: t("Subject is required") }]}
              >
                <Input placeholder={t("Short summary")} />
              </Form.Item>
              <Form.Item
                label={t("Description")}
                name="description"
                rules={[
                  { required: true, message: t("Description is required") },
                ]}
              >
                <TextArea rows={4} placeholder={t("Describe the issue")} />
              </Form.Item>
              <Form.Item label={t("Priority")} name="priority">
                <Select
                  options={[
                    { value: "low", label: t("Low") },
                    { value: "normal", label: t("Normal") },
                    { value: "high", label: t("High") },
                  ]}
                />
              </Form.Item>
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  block
                  loading={loading}
                >
                  {t("Submit Ticket")}
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        <Col xs={24} md={14} lg={16}>
          <Card
            title={t("Support Tickets")}
            bordered={false}
            className="supportTickets"
            extra={
              <Space>
                {isSuper && (
                  <Tooltip title={t("Filter by client ID")}>
                    <Input
                      placeholder={t("Client ID")}
                      style={{ width: 180 }}
                      value={clientFilter}
                      onChange={(e) => setClientFilter(e.target.value)}
                      allowClear
                    />
                  </Tooltip>
                )}
                <Select
                  style={{ width: 160 }}
                  options={statusFilterOptions}
                  value={filters.status || ""}
                  onChange={(value) =>
                    setFilters((prev) => ({
                      ...prev,
                      status: value || undefined,
                    }))
                  }
                />
                <Button onClick={fetchTickets}>{t("Refresh")}</Button>
              </Space>
            }
          >
            <Spin spinning={loading}>
              <List
                dataSource={tickets}
                locale={{ emptyText: t("No tickets found") }}
                renderItem={(item) => (
                  <List.Item
                    actions={[
                      <Button
                        type="link"
                        onClick={() => openDetails(item)}
                        key="view"
                      >
                        {t("View")}
                      </Button>,
                      <Select
                        key="status"
                        size="small"
                        value={item.status}
                        onChange={(value) => updateStatusQuick(item, value)}
                        options={[
                          { value: "open", label: t("Open") },
                          { value: "in_progress", label: t("In Progress") },
                          { value: "closed", label: t("Closed") },
                        ]}
                      />,
                      isSuper ? (
                        <Button
                          key="delete"
                          danger
                          type="link"
                          className="support-delete-btn"
                          loading={deletingId === item._id}
                          onClick={() => confirmDelete(item)}
                        >
                          {t("Delete")}
                        </Button>
                      ) : null,
                    ]}
                  >
                    <List.Item.Meta
                      title={
                        <Space>
                          <Text strong>{item.subject}</Text>
                          <Tag color={priorityColors[item.priority]}>
                            {t(item.priority)}
                          </Tag>
                          <Badge
                            color={statusColors[item.status]}
                            text={t(item.status)}
                          />
                        </Space>
                      }
                      description={
                        <div>
                          <div>
                            {t("Updated")}:{" "}
                            {dayjs(item.updatedAt).format("DD.MM.YYYY HH:mm")}
                          </div>
                          <div>
                            {t("Client")}:{" "}
                            {item.clientId?.name || clientId || t("N/A")}
                          </div>
                          <div>
                            {t("Created by")}:{" "}
                            {item.userId
                              ? `${item.userId.firstName || ""} ${
                                  item.userId.lastName || ""
                                }`.trim() || item.userId.email
                              : item.email || t("Unknown")}
                          </div>
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            </Spin>
          </Card>
        </Col>
      </Row>

      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={selectedTicket ? selectedTicket.subject : t("Ticket")}
        width={600}
        destroyOnClose
        className="supportDrawer"
        extra={
          isSuper && selectedTicket ? (
            <Button
              danger
              className=""
              loading={deletingId === selectedTicket._id}
              onClick={() => confirmDelete(selectedTicket)}
            >
              {t("Delete")}
            </Button>
          ) : null
        }
      >
        {selectedTicket ? (
          <Space direction="vertical" style={{ width: "100%" }} size="large">
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label={t("Status")}>
                <Badge
                  color={statusColors[selectedTicket.status]}
                  text={t(selectedTicket.status)}
                />
              </Descriptions.Item>
              <Descriptions.Item label={t("Priority")}>
                <Tag color={priorityColors[selectedTicket.priority]}>
                  {t(selectedTicket.priority)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t("Created by")}>
                {selectedTicket.userId
                  ? `${selectedTicket.userId.firstName || ""} ${
                      selectedTicket.userId.lastName || ""
                    }`.trim() || selectedTicket.userId.email
                  : selectedTicket.email || t("Unknown")}
              </Descriptions.Item>
              <Descriptions.Item label={t("Email")}>
                {selectedTicket.email || userEmail || "-"}
              </Descriptions.Item>
              <Descriptions.Item label={t("Created")}>
                {dayjs(selectedTicket.createdAt).format("DD.MM.YYYY HH:mm")}
              </Descriptions.Item>
            </Descriptions>

            <div>
              <Title level={5}>{t("Messages")}</Title>
              <List
                dataSource={selectedTicket.messages || []}
                renderItem={(msg, idx) => (
                  <List.Item key={idx}>
                    <Space direction="vertical" style={{ width: "100%" }}>
                      <Space align="center">
                        <Text strong>{msg.author}</Text>
                        <Text type="secondary">
                          {dayjs(msg.createdAt).format("DD.MM.YYYY HH:mm")}
                        </Text>
                      </Space>
                      <Paragraph style={{ marginBottom: 0 }}>
                        {msg.message}
                      </Paragraph>
                    </Space>
                  </List.Item>
                )}
              />
            </div>

            <div>
              <Title level={5}>{t("Reply / Update Status")}</Title>
              <Form layout="vertical" form={replyForm} onFinish={addReply}>
                <Form.Item
                  label={t("Message")}
                  name="message"
                  rules={[
                    { required: true, message: t("Please enter a message") },
                  ]}
                >
                  <TextArea rows={3} placeholder={t("Write a reply")} />
                </Form.Item>
                <Form.Item label={t("Status")} name="status" initialValue="">
                  <Select
                    allowClear
                    placeholder={t("Keep current status")}
                    options={[
                      { value: "open", label: t("Open") },
                      { value: "in_progress", label: t("In Progress") },
                      { value: "closed", label: t("Closed") },
                    ]}
                  />
                </Form.Item>
                <Form.Item>
                  <Space>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={replyLoading}
                    >
                      {t("Send")}
                    </Button>
                    <Button onClick={() => replyForm.resetFields()}>
                      {t("Reset")}
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            </div>
          </Space>
        ) : (
          <Spin />
        )}
      </Drawer>
    </div>
  );
};

export default Support;
